import axios from "axios"
import { server } from "./config"
import secureLocalStorage from "react-secure-storage"
import moment from "moment"
import * as Sentry from "@sentry/react"
import axiosRetry from "axios-retry"

const client = axios.create({
  baseURL: server,
  timeout: 60000, // 1 minute
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
})

axiosRetry(client, {
  retries: 2,
  retryDelay: (retryCount) => {
    return retryCount === 1 ? 500 : 1000
  },
  retryCondition: (error) => {
    return (
      axiosRetry.isNetworkOrIdempotentRequestError(error) ||
      error.response?.status === 504
    )
  },
  onRetry: (retryCount, error, requestConfig) => {
    Sentry.addBreadcrumb({
      category: "retry",
      message: `Retrying ${
        requestConfig.url
      } (Attempt ${retryCount}) due to error ${
        error.response?.status || error.code
      }`,
      level: "info",
    })
  },
  shouldResetTimeout: true,
})

// Detectamos si es Chrome 144 o Chromebook (CrOS)
const ua = navigator.userAgent
if (ua.includes("Chrome/144") || ua.includes("CrOS")) {
  // Eliminamos estas propiedades de los defaults usando dot notation
  // para cumplir con la regla de ESLint.
  // Esto evita que Axios intente forzar headers que Chrome considera "unsafe".
  delete client.defaults.headers.common.Connection
  delete client.defaults.headers.common["Keep-Alive"] // Usamos corchetes aquí porque tiene un guion
}

// add interceptor so the http client can add auth token to headers
client.interceptors.request.use(
  (config) => {
    // Guardamos el estado actual en la configuración de la petición
    config.metadata = {
      startTime: Date.now(),
      visibilityAtStart: document.visibilityState,
    }
    return new Promise((resolve) => {
      const authToken = secureLocalStorage.getItem("smashAuthToken")
      if (!config.headers.Authorization && authToken) {
        config.headers.Authorization = `${authToken}`
      }
      resolve(config)
    })
  },
  (error) => {
    return Promise.reject(error)
  }
)

const isoDateFormat = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?$/

// server timezone is America/Vancouver
const SERVER_TIMEZONE = "America/Vancouver"

function getTimezoneOffset(timeZone = SERVER_TIMEZONE, date = new Date()) {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
      hour12: false,
    }).formatToParts(date)

    const getPart = (type) => parts.find((p) => p.type === type).value

    const year = parseInt(getPart("year"), 10)
    const month = parseInt(getPart("month"), 10)
    const day = parseInt(getPart("day"), 10)
    const hour = parseInt(getPart("hour"), 10)
    const minute = parseInt(getPart("minute"), 10)
    const second = parseInt(getPart("second"), 10)

    // This represents the local time as if it were UTC
    const targetTime = Date.UTC(year, month - 1, day, hour, minute, second)
    // The actual UTC time
    const utcTime = Date.UTC(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate(),
      date.getUTCHours(),
      date.getUTCMinutes(),
      date.getUTCSeconds()
    )

    const offsetMinutes = Math.round((targetTime - utcTime) / 60000)
    const absOffset = Math.abs(offsetMinutes)
    const hours = Math.floor(absOffset / 60)
    const mins = absOffset % 60
    const sign = offsetMinutes >= 0 ? "+" : "-"

    return `${sign}${String(hours).padStart(2, "0")}:${String(mins).padStart(
      2,
      "0"
    )}`
  } catch (e) {
    console.warn(`Error calculating offset for ${timeZone}`, e)
    // If the requested timezone failed, try to assume America/Vancouver
    if (timeZone !== SERVER_TIMEZONE) {
      try {
        return getTimezoneOffset(SERVER_TIMEZONE, date)
      } catch (err) {
        return "-07:00" // Ultimate fallback
      }
    }
    return "-07:00" // Fallback if calculation for Vancouver itself failed
  }
}

function parseDatesAsServerTime(obj) {
  if (obj === null || obj === undefined || typeof obj !== "object") return obj

  const savedTimeZone = secureLocalStorage.getItem("mainTeacherTimeZone")
  const targetTimeZone =
    savedTimeZone ||
    Intl.DateTimeFormat().resolvedOptions().timeZone ||
    SERVER_TIMEZONE
  for (const key of Object.keys(obj)) {
    const value = obj[key]
    if (typeof value === "string" && isoDateFormat.test(value)) {
      if (value.startsWith("0001-01-01") || !moment(value).isValid()) {
        continue
      }

      const serverOffset = getTimezoneOffset(SERVER_TIMEZONE, new Date(value))
      const serverTime = value + serverOffset
      const offset = getTimezoneOffset(targetTimeZone, new Date(serverTime))
      obj[key] = moment(serverTime)
        .utcOffset(offset)
        .format("YYYY-MM-DDTHH:mm:ss")
    } else if (typeof value === "object") {
      parseDatesAsServerTime(value)
    }
  }
  return obj
}

// Add response interceptor to capture errors and send to Sentry
client.interceptors.response.use(
  (response) => {
    if (response && response.data) {
      parseDatesAsServerTime(response.data)
    }
    return response
  },
  (error) => {
    // Only send to Sentry in non-development environments
    if (process.env.REACT_APP_ENVIORNMENT !== "development") {
      // Si no hay 'response', es un timeout del navegador, no del gateway
      if (!error.response && error.code === "ECONNABORTED") {
        Sentry.setTag("timeout_type", "client_side_abort")
      } else if (error.response?.status === 504) {
        // Si hay response, el 504 vino de tu Nginx/Cloudflare
        Sentry.setTag("timeout_type", "server_side_gateway")
      }
      const { config, response, code, message } = error

      // Extraemos los estados de visibilidad
      const visibilityAtEnd = document.visibilityState
      const visibilityAtStart = config?.metadata?.visibilityAtStart || "unknown"

      // Capture additional network context
      const isOnline = navigator.onLine
      const connectionType = navigator.connection?.effectiveType || "unknown"
      const downlink = navigator.connection?.downlink || "unknown"
      const rtt = navigator.connection?.rtt || "unknown"

      // Calculate request timing if available
      const requestStartTime = config?.metadata?.startTime
      const requestDuration = requestStartTime
        ? Date.now() - requestStartTime
        : "unknown"

      // Determine error category
      let errorCategory = "unknown"
      if (!response && code === "ETIMEDOUT") {
        errorCategory = "timeout"
      } else if (!response && !isOnline) {
        errorCategory = "offline"
      } else if (!response) {
        errorCategory = "network_failure"
      } else if (response?.status >= 500) {
        errorCategory = "server_error"
      }

      // Prepare enhanced error context
      const errorContext = {
        // Request details
        endpoint: config?.url,
        duration: config?.metadata?.startTime
          ? `${Date.now() - config.metadata.startTime}ms`
          : "unknown",
        method: config?.method?.toUpperCase(),
        baseURL: config?.baseURL,
        fullURL: config?.url ? `${config.baseURL}${config.url}` : undefined,
        timeout: config?.timeout,

        // Response details
        statusCode: response?.status,
        statusText: response?.statusText,
        errorCode: code,
        errorMessage: message,
        responseData: response?.data,
        requestData: config?.data,

        // Network context
        isOnline: isOnline,
        connectionType: connectionType,
        downlink: `${downlink} Mbps`,
        rtt: `${rtt} ms`,

        // Timing
        requestDuration: `${requestDuration} ms`,

        // Browser context
        userAgent: navigator.userAgent,
        platform: navigator.platform,

        // Error classification
        errorCategory: errorCategory,
      }

      // Determinamos si es un error crítico de Chrome 144 para forzar el envío
      const isChrome144Issue =
        ua.includes("Chrome/144") &&
        (response?.status === 504 || code === "ETIMEDOUT")

      // Check if request was cancelled by user
      const wasUserCancelled =
        code === "ECONNABORTED" ||
        code === "ERR_CANCELED" ||
        config?.signal?.aborted ||
        message?.toLowerCase()?.includes("cancel") ||
        message?.toLowerCase()?.includes("abort")

      // Check if page is being unloaded (user navigating away)
      const isPageUnloading =
        document.visibilityState === "hidden" ||
        document.hidden ||
        !document.hasFocus()

      // Check if this is a network error (no response received)
      // const isNetworkError = !response && !wasUserCancelled

      // Sample network errors to reduce volume (only send 20%)
      // const shouldSampleNetworkError = isNetworkError && Math.random() < 0.2

      // Determine if we should send this error to Sentry
      const shouldSendToSentry =
        !wasUserCancelled &&
        isOnline &&
        // MODIFICACIÓN: Enviamos si la página está activa O si es un posible error de Chrome 144
        (!isPageUnloading || isChrome144Issue) &&
        (response?.status >= 500 || code === "ETIMEDOUT")

      if (shouldSendToSentry) {
        Sentry.captureException(error, {
          tags: {
            type: "network_error",
            endpoint: config?.url,
            is_background_error: isPageUnloading ? "true" : "false",
            chrome_version: ua.includes("Chrome/144") ? "144" : "other",
            method: config?.method?.toUpperCase(),
            statusCode: response?.status || "no_response",
            errorCategory: errorCategory,
            isOnline: isOnline.toString(),
            connectionType: connectionType,
            visibility_start: visibilityAtStart,
            visibility_end: visibilityAtEnd,
            tab_switched:
              visibilityAtStart !== visibilityAtEnd ? "true" : "false",
            timeout_type:
              !error.response && error.code === "ECONNABORTED"
                ? "client_side_abort"
                : error.response?.status === 504
                ? "server_side_gateway"
                : "unknown",
          },
          contexts: {
            axios: errorContext,
          },
          level: response?.status >= 500 ? "error" : "warning",
          fingerprint: [
            errorCategory,
            config?.method?.toUpperCase(),
            config?.url,
            response?.status?.toString() || "no_response",
          ],
        })
      }
    }

    return Promise.reject(error)
  }
)

export default client
